import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMilestoneById, deleteMilestone } from '../api/milestones';
import { getProjectById } from '../api/projects';
import { getProgrammeById } from '../api/programmes';
import { getTasks } from '../api/tasks';
import { Milestone } from '../types/milestone';
import { Project } from '../types/project';
import { Task } from '../types/task';
import MilestoneForm from '../components/MilestoneForm';
import TaskForm from '../components/TaskForm';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Card from '../components/Card';
import EntityDetailLayout from '../components/detail/EntityDetailLayout';
import DetailChildList, { DetailRow } from '../components/detail/DetailChildList';

const formatDate = (dateString?: string): string =>
  dateString ? new Date(dateString).toLocaleDateString() : 'No due date';

const formatStatus = (status: string): string =>
  status.replace('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const MilestoneDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [milestone, setMilestone] = useState<Milestone | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [programmeName, setProgrammeName] = useState<string | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [addingTask, setAddingTask] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [milestoneData, tasksData] = await Promise.all([
        getMilestoneById(id),
        getTasks(undefined, id),
      ]);
      setMilestone(milestoneData);
      setTasks(tasksData);

      const projectData = await getProjectById(milestoneData.project_id);
      setProject(projectData);
      if (projectData.programme_id) {
        try {
          const programme = await getProgrammeById(projectData.programme_id);
          setProgrammeName(programme.name);
        } catch {
          setProgrammeName(null);
        }
      } else {
        setProgrammeName(null);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load milestone');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!milestone) return;
    if (
      !window.confirm(
        `Delete milestone "${milestone.name}"? This will unset milestone_id for all associated tasks.`
      )
    ) {
      return;
    }
    try {
      await deleteMilestone(milestone.id);
      navigate(`/projects/${milestone.project_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete milestone');
    }
  };

  const completedTasks = tasks.filter((t) => t.status === 'completed').length;

  const breadcrumb = [
    { label: 'Programmes', path: '/programmes' },
    ...(project?.programme_id
      ? [{ label: programmeName ?? 'Programme', path: `/programmes/${project.programme_id}` }]
      : []),
    ...(project ? [{ label: project.name, path: `/projects/${project.id}` }] : []),
    { label: milestone?.name ?? 'Milestone' },
  ];

  const taskRows: DetailRow[] = tasks.map((task) => ({
    id: task.id,
    to: `/tasks/${task.id}`,
    title: task.title,
    subtitle: task.description ?? undefined,
    badges: (
      <>
        <Badge priority={task.priority as any} size="sm">
          {task.priority}
        </Badge>
        <Badge status={task.status as any} size="sm">
          {task.status.replace('_', ' ')}
        </Badge>
      </>
    ),
    meta: task.due_date ? `Due ${formatDate(task.due_date)}` : undefined,
  }));

  return (
    <>
      <EntityDetailLayout
        breadcrumb={breadcrumb}
        loading={loading}
        error={error}
        title={milestone?.name ?? ''}
        subtitle={
          milestone ? `Target date: ${formatDate(milestone.target_date)}` : undefined
        }
        headerBadges={
          milestone ? (
            <Badge status={(milestone.status || 'upcoming') as any} size="md">
              {formatStatus(milestone.status ?? 'upcoming')}
            </Badge>
          ) : undefined
        }
        metrics={
          milestone
            ? [
                { label: 'Total Tasks', value: tasks.length, accent: 'blue' },
                { label: 'Completed', value: completedTasks, accent: 'green' },
              ]
            : undefined
        }
        progress={
          milestone ? { percentage: milestone.completion_percentage ?? 0 } : undefined
        }
        actions={
          milestone ? (
            <>
              <Button variant="primary" size="sm" onClick={() => setAddingTask(true)}>
                Add Task
              </Button>
              <Button variant="primary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete}>
                Delete
              </Button>
            </>
          ) : undefined
        }
      >
        <DetailChildList
          title="Tasks"
          rows={taskRows}
          emptyMessage="No tasks linked to this milestone yet."
        />
      </EntityDetailLayout>

      {editing && milestone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-2xl my-8">
            <Card variant="glass">
              <Card.Header title="Edit Milestone" />
              <Card.Body>
                <MilestoneForm
                  milestone={milestone}
                  onSuccess={() => {
                    setEditing(false);
                    load();
                  }}
                  onCancel={() => setEditing(false)}
                />
              </Card.Body>
            </Card>
          </div>
        </div>
      )}

      {addingTask && milestone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-2xl my-8">
            <TaskForm
              defaultProjectId={milestone.project_id}
              defaultMilestoneId={milestone.id}
              lockProjectMilestone
              onSuccess={() => {
                setAddingTask(false);
                load();
              }}
              onCancel={() => setAddingTask(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MilestoneDetailPage;
